import { useQuery } from '@tanstack/react-query';
import axios from 'axios';
import qs from 'qs';
import { AREA_NAMES } from '../common/constants';

// -------------------PT / Jobs --------------------

const QUERY_PT_PARTCLASS = ['pt/partClass'];
export const useGetActiveParts = () => {
  return useQuery({
    queryKey: QUERY_PT_PARTCLASS,
    queryFn: async () => (await axios.get(`pt/partClass?type=${true}`, { loaderRequired: false })).data,
  });
};

const QUERY_PT_KIT_PART = 'PT/Kits/Parts';
export const useGetKitPart = (params) =>
  useQuery({
    queryKey: [QUERY_PT_KIT_PART, params.kitId],
    queryFn: async ({ queryKey }) => (await axios.get(`PT/Kits/${queryKey[1]}/Parts`, { loaderRequired: false })).data,
  });

const QUERY_PT_HardwareParts = 'PT/Parts/PT/HardwareParts';
export const useGetActiveHardwareParts = () => {
  return useQuery({
    queryKey: [QUERY_PT_HardwareParts],
    queryFn: async () => (await axios.get(`PT/Parts/PT/HardwareParts`, { loaderRequired: false })).data,
  });
};

const QUERY_JOB_CUT_PARTS = 'pt/partclass/parts';
export const useCutParts = (partclassId) => {
  return useQuery({
    queryKey: [QUERY_JOB_CUT_PARTS, partclassId],
    queryFn: async () => (await axios.get(`PT/partclass/${partclassId}/parts`, { loaderRequired: false })).data,
  });
};

const QUERY_PT_CUTSTATUS = 'pt/cutStatus';
export const useCutStatus = () =>
  useQuery({
    queryKey: [QUERY_PT_CUTSTATUS],
    queryFn: async () => (await axios.get('pt/cutstatus', { loaderRequired: false })).data,
  });

const QUERY_PT_BIDITEMS = 'Jobs/biditems';
export const useBidItems = () =>
  useQuery({
    queryKey: [QUERY_PT_BIDITEMS],
    queryFn: async () => (await axios.get('Jobs/biditems', { loaderRequired: false })).data,
  });

const QUERY_PT_EXCUSECODES = 'pt/excusecodes';
export const useExcuseCodes = () =>
  useQuery({
    queryKey: [QUERY_PT_EXCUSECODES],
    queryFn: async () => (await axios.get('pt/excusecodes', { loaderRequired: false })).data,
  });

const QUERY_PT_ACCESSORIES = 'common/accessories';
export const useAccessories = () =>
  useQuery({
    queryKey: [QUERY_PT_ACCESSORIES],
    queryFn: async () => (await axios.get('common/accessories', { loaderRequired: false })).data,
  });

const QUERY_PT_MILLTHICKNESS = 'pt/millthickness';
export const useMillthickness = () =>
  useQuery({
    queryKey: QUERY_PT_MILLTHICKNESS,
    queryFn: async () => {
      const response = await axios.get('pt/millthickness');
      return response.data;
    },
  });

const QUERY_PT_CABLEDIAMETER = 'pt/cableDiameter';
export const useCableDiameter = () =>
  useQuery({
    queryKey: QUERY_PT_CABLEDIAMETER,
    queryFn: async () => (await axios.get('pt/cableDiameter', { loaderRequired: false })).data,
    refetchIntervalInBackground: false,
  });

const QUERY_PT_ADDITIONAL_ITEMS = ['pt/additionalItems'];
export const useAdditionalItems = () =>
  useQuery({
    queryKey: QUERY_PT_ADDITIONAL_ITEMS,
    queryFn: async () => (await axios.get(`pt/additionalItems`, { loaderRequired: false })).data,
  });

const QUERY_PT_DELIVERYTYPES = ['pt/deliveryTypes'];
export const useDeliveryTypes = () =>
  useQuery({
    queryKey: QUERY_PT_DELIVERYTYPES,
    queryFn: async () => (await axios.get(`pt/deliveryTypes`, { loaderRequired: false })).data,
  });

// job details query
const QUERY_JOBS_DETAILS_BYJOBID = 'jobs/jobId';
export const useJob = (params) =>
  useQuery({
    queryKey: [QUERY_JOBS_DETAILS_BYJOBID, params.jobId],
    queryFn: async ({ queryKey }) => (await axios.get(`jobs/${queryKey[1]}`, { loaderRequired: false })).data,
  });

// Approval Count query
const QUERY_CURRENTUSER_APPROVALCOUNT = ['approvals/approvalCount'];
export const useApprovalCount = () =>
  useQuery({
    queryKey: QUERY_CURRENTUSER_APPROVALCOUNT,
    queryFn: async () => (await axios.get(`approvals/approvalCount`, { loaderRequired: false })).data,
  });

// My Jobs
const QUERY_MY_JOBS = 'myjobs';
export const useMyJobs = (module, enabled = true) => {
  let url = '';
  let params = null;

  // based on module fetch the my jobs card data.
  if (module === AREA_NAMES.pt || module === AREA_NAMES.rebar) {
    url = 'fab/jobs/myjobs';
    params = {
      ...(module === AREA_NAMES.pt && { hasPT: true }),
      ...(module === AREA_NAMES.rebar && { hasRebar: true }),
    };
  } else if (module === AREA_NAMES.projections) {
    url = 'projections/myjobs';
  } else if (module === AREA_NAMES.submittals) {
    url = 'submittals/myjobs';
  } else {
    throw new Error('useMyJobs param module not matched.');
  }

  return useQuery({
    queryKey: [QUERY_MY_JOBS, { ...params }],
    queryFn: async () =>
      (await axios.get(url, { loaderRequired: false, ...(params && { params: { ...params } }) })).data,
    refetchOnMount: 'always',
    enabled,
  });
};

export const userDetailQuery = {
  queryKey: ['user'],
  queryFn: async () => {
    const response = await axios.get('users/me', { loaderRequired: false });
    return response.data;
  },
};

const QUERY_STUDDIAMETER = 'pt/StudDiameter';
export const useStudDiameter = () =>
  useQuery({
    queryKey: [QUERY_STUDDIAMETER],
    queryFn: async () => (await axios.get('pt/StudDiameter', { loaderRequired: false })).data,
  });

const QUERY_JOBTYPES = 'Common/JobTypes';
export const useJobTypes = () =>
  useQuery({
    queryKey: [QUERY_JOBTYPES],
    queryFn: async () => (await axios.get('Common/JobTypes')).data,
  });

export const useSentViaOptions = (type) => {
  return useQuery({
    queryKey: ['SendOptions', type],
    queryFn: async () => (await axios.get('common/SendOptions', { params: { type }, loaderRequired: false })).data,
  });
};

const QUERY_CABLE_TYPE = 'pt/CableType';
export const useCableType = () =>
  useQuery({
    queryKey: [QUERY_CABLE_TYPE],
    queryFn: async () => (await axios.get('pt/CableType')).data,
  });

const QUERY_LOCATION = 'Location';
export const useLocations = ({ ptOnly = null, activeOnly = null } = {}) => {
  const params = { ...(ptOnly !== null && { ptOnly }), ...(activeOnly !== null && { activeOnly }) };
  return useQuery({
    queryKey: [QUERY_LOCATION, params],
    queryFn: async () => (await axios.get('Locations', { params, loaderRequired: false })).data,
  });
};

const QUERY_PT_AREACODES = ['pt/areaCodes'];
export const usePTAreaCodes = () =>
  useQuery({
    queryKey: QUERY_PT_AREACODES,
    queryFn: async () => (await axios.get('pt/areaCodes')).data,
    refetchIntervalInBackground: false,
  });

const QUERY_PT_VENDORS = ['pt/Vendors'];
export const usePTVendors = () =>
  useQuery({
    queryKey: QUERY_PT_VENDORS,
    queryFn: async () => (await axios.get('pt/Vendors')).data,
    refetchIntervalInBackground: false,
  });

export const QUERY_MESSAGES_UNREAD_COUNT = ['me/messages/unreadCount'];
export const useMessagesUnreadCount = () => {
  return useQuery({
    queryKey: QUERY_MESSAGES_UNREAD_COUNT,
    queryFn: async () => (await axios.get('me/unreadCount', { loaderRequired: false })).data,
    staleTime: 1000 * 60 * 10, // data is fresh for 10 min.
    refetchInterval: 1000 * 60, // refetch data every 1 min. (need to refetch here)
  });
};

//get the job contacts, params - jobId, isActive, roleIds
export const QUERY_JOB_JOBCONTACTS = ['fab', 'jobContacts'];
export const useJobContacts = ({ jobId, active, roleIds }) =>
  useQuery({
    queryKey: [...QUERY_JOB_JOBCONTACTS, jobId, ...roleIds],
    queryFn: async () =>
      (
        await axios.get(`fab/${jobId}/jobContacts`, {
          loaderRequired: false,
          params: { active, roleIds },
          paramsSerializer: (params) => qs.stringify(params, { arrayFormat: 'repeat' }),
        })
      ).data?.value,
    refetchOnMount: 'always',
  });

export const QUERY_JOB_CUSTOMERCONTACTS = ['fab', 'customercontacts'];
export const useCustomerContacts = ({ jobId }) =>
  useQuery({
    queryKey: [...QUERY_JOB_CUSTOMERCONTACTS, jobId],
    queryFn: async () =>
      (
        await axios.get(`fab/${jobId}/customercontacts`, {
          loaderRequired: false,
        })
      ).data?.value,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

export const QUERY_JOB_CUSTOMERINFO = ['jobs', 'customerInfo'];
export const useCustomerInfo = ({ jobId, enabled = true } = {}) =>
  useQuery({
    queryKey: [...QUERY_JOB_CUSTOMERINFO, jobId],
    queryFn: async () => (await axios.get(`jobs/${jobId}/customerInfo`, { loaderRequired: false })).data,
    enabled: enabled && !!jobId,
    staleTime: Infinity,
    refetchOnMount: false,
    refetchOnWindowFocus: false,
    refetchOnReconnect: false,
  });

// ---------------------------- projections -------------------------------------------

export const QUERY_PROJECTIONS_GRADES = 'grades';
export const useGrades = (locationId) => {
  return useQuery({
    queryKey: [QUERY_PROJECTIONS_GRADES, locationId],
    queryFn: async () => (await axios.get(`locations/${locationId}/grades`, { loaderRequired: false })).data,
    enabled: !!locationId,
  });
};

export const QUERY_PROJECTIONS_COATINGS = 'coatings';
export const useCoatings = (locationId) => {
  return useQuery({
    queryKey: [QUERY_PROJECTIONS_COATINGS, locationId],
    queryFn: async () => (await axios.get(`locations/${locationId}/coatings`, { loaderRequired: true })).data,
    enabled: !!locationId,
  });
};

export const QUERY_PROJECTIONS_BARSIZES = 'barsizes';
export const useBarSizes = (locationId) => {
  return useQuery({
    queryKey: [QUERY_PROJECTIONS_BARSIZES, locationId],
    queryFn: async () => (await axios.get(`locations/${locationId}/barsizes`)).data,
    enabled: !!locationId,
  });
};

// projections controllers gives UNDEFINED - 0 value
export const QUERY_PROJECTIONS_SIZES = 'sizes';
export const useSizes = (locationId) => {
  return useQuery({
    queryKey: [QUERY_PROJECTIONS_BARSIZES, locationId],
    queryFn: async () => (await axios.get(`projections/${locationId}/size`, { loaderRequired: true })).data,
    enabled: !!locationId,
  });
};

//--------------------------------- detailing time entry

export const QUERY_CATEGORIES = 'timeentry-categories';
export const useCategories = () => {
  return useQuery({
    queryKey: [QUERY_CATEGORIES],
    queryFn: async () => (await axios.get(`TimeEntries/categories`, { loaderRequired: false })).data,
  });
};

// -------------------------------- Submittals

export const useSubmittalStatus = () => {
  return useQuery({
    queryKey: ['submittal-status'],
    queryFn: async () => (await axios.get(`submittals/statuses`, { loaderRequired: false })).data,
  });
};

export const useControlCodes = (jobId) => {
  return useQuery({
    queryKey: ['controlcodes', jobId],
    queryFn: async () => (await axios.get(`/submittals/${jobId}/controlcodes`)).data,
    refetchOnMount: 'always',
  });
};

export const useSubmittalTypes = () => {
  return useQuery({
    queryKey: ['submittal', 'types'],
    queryFn: async () => (await axios.get(`/submittals/types`, { loaderRequired: false })).data,
  });
};
